/**
 * Onboarding deferred — "Almost there, motek".
 *
 * Information page, NOT celebration. Reached when the user clicks Done
 * with the "I'll set up WhatsApp later" checkbox checked (or skipped any
 * earlier step on the path here).
 *
 * Shows Frame H promises as future-state (open coral circles, never lit
 * on this page). Bottom CTA opens the dashboard, where the DashboardBanners
 * carries the persistent "come back and finish this" reminder.
 *
 * No 🎉 emoji (reserved for actual completion). Motek leak preserved.
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/useAuth';
import { computeRailState } from '@/lib/onboardingState';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';

const PROMISES = [
  'Every WhatsApp contact matched to the ad',
  'Conversions fire to Google Ads + Meta Ads automatically',
  'Your customer list, yours to export',
  'ROAS climbs every month',
];

export function OnboardingSkippedPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  return (
    <OnboardingShell
      current={4}
      title="Almost there, motek"
      subtitle="When you finish setting up, you'll get:"
      state={computeRailState(me ?? null)}
    >
      <div className="space-y-6">
        {/* Future-state promise list — open circles, never lit on this page */}
        <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
          <ul className="space-y-4">
            {PROMISES.map((text) => (
              <li key={text} className="flex items-start gap-3.5">
                <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/5">
                  <span className="block h-2 w-2 rounded-full border-2 border-brand/40" />
                </span>
                <span className="text-base text-dark leading-snug">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Instruction card — how to finish later */}
        <div className="rounded-2xl bg-bg-tint border border-dark/5 p-5">
          <p className="text-sm text-dark leading-relaxed">
            <strong>Finish anytime.</strong> Open <span className="font-semibold">Integrations</span> from your dashboard to connect what's still pending.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex w-full items-center justify-center rounded-full bg-brand px-6 py-4 text-lg font-bold text-white hover:bg-brand-dark transition-colors"
        >
          Open your dashboard
        </button>
      </div>
    </OnboardingShell>
  );
}
